import { css } from '@emotion/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { getMentoringProgramReflectionSummary } from 'redux/actions/mentoring-program-reflections';
import { getMentoringProgramReflectionStats } from 'redux/selectors/mentoring-program-reflections';
import { textSmallLineHeight } from 'styles/global_defaults/fonts';
import { heatwave } from 'athena/styles/colors';

const styles = css`
  .stats {
    gap: 32px;
  }

  .stat-number {
    color: ${heatwave};
  }

  .stat-label {
    line-height: ${textSmallLineHeight}px;
  }
`;

type ReflectionStatsProps = {
  programId: number;
  connectionId: number;
  programName: string;
};

const ReflectionStats = ({ programId, connectionId, programName }: ReflectionStatsProps) => {
  const dispatch = useAppDispatch();
  const { timeTogether, totalSessions, totalHours } = useSelector(
    (state) => getMentoringProgramReflectionStats(state, connectionId),
  );
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REFLECTION_WORKSPACE;

  useEffect(() => {
    dispatch(getMentoringProgramReflectionSummary({ programId, connectionId }));
  }, [programId, connectionId, dispatch]);

  const timeTogetherLabel = {
    months: translationBase.STATS.MONTHS(timeTogether.value),
    weeks: translationBase.STATS.WEEKS(timeTogether.value),
    days: translationBase.STATS.DAYS(timeTogether.value),
  }[timeTogether.unit];

  const statItems = [
    { key: 'timeTogether', value: timeTogether.value, label: timeTogetherLabel },
    { key: 'sessions', value: totalSessions, label: translationBase.STATS.SESSIONS(totalSessions) },
    { key: 'hours', value: totalHours, label: translationBase.STATS.HOURS(totalHours) },
  ];

  return (
    <div css={styles}>
      <p className='text-regular black semi-bold mb-3'>{translationBase.HEADER()}</p>
      <div className='heading-1 black mb-3'>{programName}</div>
      <div className='d-flex justify-content-center mb-5 stats'>
        {statItems.map(({ key, value, label }) => (
          <div key={key} className='d-flex flex-column align-items-center'>
            <span className='stat-number header-xl bold'>{value}</span>
            <span className='stat-label text-regular semi-bold gray-2'>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReflectionStats;
